import ReactJsonView from '@microlink/react-json-view'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Play, RotateCcw } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import TaskNotes from 'components/reusable/TaskNotes'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Label } from 'components/ui/label'
import { Switch } from 'components/ui/switch'
import { Badge, Button, CollapsibleContainer } from 'slab'
import { getFormattedDate } from 'utils'
import {
  getBrokersTasksByIdTaskIdPlaywrightCommand,
  getGetBrokersTasksByIdTaskIdResultsQueryKey,
  getGetBrokersTasksTaskIdQueryKey,
  getGetBrokersTaskWorkflowsIdQueryKey,
  useDeleteBrokersTasksByIdTaskIdLease,
  useGetBrokersTasksByIdTaskIdResults,
  useGetBrokersTasksTaskId,
  usePatchBrokersTasks,
  usePostBrokersTasksByIdTaskIdExecute,
  usePostBrokersTasksByIdTaskIdResetToPending,
} from 'utils/web_api'
import { type BrokersTask, type BrokersTaskDetail } from 'utils/web_api.schemas'

import AuditHistoryDrawer from './AuditHistoryDrawer'
import TaskActionControls from './TaskActionControls'
import TaskResultExplorer from './TaskResultExplorer'
import {
  ExecutionContextBadge,
  ExecutionMethodBadge,
  TaskStatusBadge,
} from './WorkflowVisualIndicators'

type TaskDetailsDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  taskId: string | null
  workflowId: string
  enrollmentId: string
  handleTaskAction: (taskId: string, action: string) => void
  taskActionLoading: boolean
}

const POLLING_INTERVAL_MS = 3000

const TaskDetailsDialog = ({
  open,
  onOpenChange,
  taskId,
  workflowId,
  enrollmentId,
  handleTaskAction,
  taskActionLoading,
}: TaskDetailsDialogProps) => {
  const queryClient = useQueryClient()
  const enabled = open && Boolean(taskId)
  const taskIdOrFallback = taskId ?? ''

  const taskQuery = useGetBrokersTasksTaskId(taskIdOrFallback, {
    query: {
      enabled,
      refetchInterval: (data) => {
        if (!enabled) return false
        const isProcessing = data.state.data?.status &&
          data.state.data.status === 'processing'
        return isProcessing ? POLLING_INTERVAL_MS : false
      },
      refetchIntervalInBackground: false,
    },
  })
  const taskDetail = taskQuery.data
  const taskLoading = taskQuery.isLoading

  // Only poll when task is in an active/processing state
  const isTaskProcessing = taskDetail?.status === 'processing'
  const shouldPoll = enabled && isTaskProcessing

  const taskResultsQuery = useGetBrokersTasksByIdTaskIdResults(taskIdOrFallback, {
    query: {
      enabled,
      refetchInterval: shouldPoll ? POLLING_INTERVAL_MS : false,
      refetchIntervalInBackground: false,
    },
  })
  const taskResults = taskResultsQuery.data ?? []

  const [executionHistoryVersion, setExecutionHistoryVersion] = useState(0)
  const [auditSectionOpen, setAuditSectionOpen] = useState(false)
  const [includeSafety, setIncludeSafety] = useState(true)
  const [ignoreHold, setIgnoreHold] = useState(false)

  const executeTaskMutation = usePostBrokersTasksByIdTaskIdExecute()
  const resetTaskMutation = usePostBrokersTasksByIdTaskIdResetToPending()
  const clearLeaseMutation = useDeleteBrokersTasksByIdTaskIdLease()
  const buildPlaywrightCommandMutation = useMutation( // Avoids fetching unless the command is actually needed
    {
      mutationKey: ['build-playwright-command', taskId],
      mutationFn: async ({ taskId, includeSafety, ignoreHold }: { taskId: string; includeSafety: boolean; ignoreHold: boolean }) => getBrokersTasksByIdTaskIdPlaywrightCommand(taskId, { safety: includeSafety, ignore_hold: ignoreHold }),
    }
  )
  const bulkUpdateMutation = usePatchBrokersTasks()

  const handleManualRefresh = async () => {
    await Promise.all([
      taskQuery.refetch(),
      taskResultsQuery.refetch(),
    ])
    toast.success('Task details refreshed manually!', { position: 'top-center' })
  }

  const isRefreshing = taskQuery.isFetching || taskResultsQuery.isFetching

  useEffect(() => {
    if (!open) {
      setIncludeSafety(true)
      setIgnoreHold(false)
      setAuditSectionOpen(false)
    }
  }, [open])

  const metadata = useMemo(() => {
    return (taskDetail?.metadata ?? {}) as Record<string, unknown>
  }, [taskDetail?.metadata])

  const instructions = useMemo(() => {
    const value = metadata?.instructions
    return typeof value === 'string' ? value : undefined
  }, [metadata])

  const runbookUrl = useMemo(() => {
    const urlCandidates = [
      metadata?.runbook_url,
      metadata?.notion_runbook_url,
      metadata?.notion_url,
    ]
    const firstUrl = urlCandidates.find(
      (candidate) => typeof candidate === 'string' && candidate.length > 0
    )
    return typeof firstUrl === 'string' ? firstUrl : undefined
  }, [metadata])

  const scribeUrl = useMemo(() => {
    const value = metadata?.scribe_url
    return typeof value === 'string' ? value : undefined
  }, [metadata])

  const invalidateTaskQueries = async () => {
    if (!taskId) return

    const promises: Array<Promise<unknown>> = [
      queryClient.invalidateQueries({
        queryKey: getGetBrokersTasksTaskIdQueryKey(taskId),
      }),
      queryClient.invalidateQueries({
        queryKey: getGetBrokersTasksByIdTaskIdResultsQueryKey(taskId),
      }),
    ]

    if (workflowId) {
      promises.push(
        queryClient.invalidateQueries({
          queryKey: getGetBrokersTaskWorkflowsIdQueryKey(workflowId),
        })
      )
    }

    await Promise.all(promises)
  }

  const handleExecuteTask = () => {
    if (!taskId) return

    executeTaskMutation.mutate(
      { taskId },
      {
        onSuccess: async () => {
          toast.success('Task execution queued', { position: 'top-center' })
          await invalidateTaskQueries()
        },
        onError: (error) => {
          const message = (error as { message?: string })?.message || 'Failed to execute task'
          toast.error(message)
        },
      }
    )
  }

  const handleResetToPending = () => {
    if (!taskId) return

    resetTaskMutation.mutate(
      { taskId },
      {
        onSuccess: async () => {
          toast.success('Task reset to pending', { position: 'top-center' })
          await invalidateTaskQueries()
        },
        onError: (error) => {
          const message = (error as { message?: string })?.message || 'Failed to reset task'
          toast.error(message)
        },
      }
    )
  }

  const handleFailTask = () => {
    if (!taskId) return

    bulkUpdateMutation.mutate(
      {
        data: {
          task_ids: [taskId],
          status_event: 'fail',
          status_message: 'Task manually moved to failed state by broker'
        }
      },
      {
        onSuccess: async () => {
          toast.success('Task moved to failed state', { position: 'top-center' })
          await invalidateTaskQueries()
        },
        onError: (error) => {
          const message = (error as { message?: string })?.message || 'Failed to fail task'
          toast.error(message)
        },
      }
    )
  }

  const handleClearLease = (force = false) => {
    if (!taskId) return

    clearLeaseMutation.mutate(
      { taskId, params: force ? { force: true } : undefined },
      {
        onSuccess: async () => {
          toast.success('Task lease cleared', { position: 'top-center' })
          await invalidateTaskQueries()
        },
        onError: (error) => {
          const message = (error as { message?: string })?.message || 'Failed to clear lease'

          if (!force && message.toLowerCase().includes('still active')) {
            const shouldForce = window.confirm('The lease is still active. Force clear it?')
            if (shouldForce) {
              handleClearLease(true)
              return
            }
          }

          toast.error(message)
        },
      }
    )
  }

  const hasPlaywrightExecution = Boolean(taskDetail?.playwright_execution_key)
  const hasLease = Boolean(taskDetail?.lease_holder || taskDetail?.lease_expires_at)

  const handleCopyPlaywrightCommand = async () => {
    if (!taskId || !hasPlaywrightExecution) {
      return
    }

    try {
      const command = await buildPlaywrightCommandMutation.mutateAsync({
        taskId,
        includeSafety,
        ignoreHold,
      })
      await navigator.clipboard.writeText(command.command)
      toast.success('Playwright command copied to clipboard', {
        position: 'top-center',
      })
    } catch (error) {
      console.error('Failed to copy command', error)
      toast.error('Unable to build Playwright command')
    }
  }

  const renderMetadataSection = (detail: BrokersTaskDetail) => {
    if (!detail.metadata || Object.keys(detail.metadata).length === 0) {
      return null
    }

    return (
      <CollapsibleContainer
        heading='Task metadata'
        headingLevel={4}
        defaultOpen={false}
      >
        <div className='p-4'>
          <ReactJsonView
            src={detail.metadata as Record<string, unknown>}
            theme={'github' as any}
            collapsed={2}
            displayDataTypes={false}
            displayObjectSize={false}
            enableClipboard={false}
          />
        </div>
      </CollapsibleContainer>
    )
  }

  const renderInstructionsSection = () => {
    if (!instructions && !scribeUrl && !runbookUrl) {
      return null
    }

    return (
      <CollapsibleContainer
        heading='Runbook & Instructions'
        headingLevel={4}
        defaultOpen={false}
      >
        <div className='space-y-3 bg-gray-50 p-4'>
          {instructions && (
            <p className='whitespace-pre-line text-sm text-gray-700'>
              {instructions}
            </p>
          )}
          <div className='flex flex-wrap gap-2 text-sm'>
            {runbookUrl && (
              <a
                href={runbookUrl}
                target='_blank'
                rel='noreferrer'
                className='text-blue-600 hover:text-blue-500'
              >
                Open runbook
              </a>
            )}
            {scribeUrl && (
              <a
                href={scribeUrl}
                target='_blank'
                rel='noreferrer'
                className='text-blue-600 hover:text-blue-500'
              >
                View scribe walkthrough
              </a>
            )}
          </div>
        </div>
      </CollapsibleContainer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden p-0'>
        <DialogHeader className='border-b border-gray-100 px-6 py-4'>
          <DialogTitle className='text-lg'>
            {taskDetail?.display_name ?? 'Task details'}
          </DialogTitle>
        </DialogHeader>

        <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
          {taskLoading && (
            <div className='flex flex-1 items-center justify-center py-16'>
              <Loader2 className='h-6 w-6 animate-spin text-gray-400' />
            </div>
          )}

          {!taskLoading && !taskDetail && (
            <div className='flex flex-1 items-center justify-center p-6 text-sm text-gray-600'>
              Unable to load task details.
            </div>
          )}

          {!taskLoading && taskDetail && (
            <div className='flex-1 overflow-y-auto px-6 py-6'>
              <div className='space-y-6'>
                <section className='rounded-lg border border-gray-200 bg-white p-5 shadow-sm'>
                  <div className='flex flex-wrap items-start justify-between gap-4'>
                    <div>
                      <div className='flex flex-wrap items-center gap-3'>
                        <TaskStatusBadge status={taskDetail.status} isExecutable={taskDetail.can_execute} />
                        <ExecutionMethodBadge method={taskDetail.execution_method} />
                        <ExecutionContextBadge context={taskDetail.execution_context} />
                        <Badge type='neutral'>
                          {taskDetail.id.slice(-6)}
                        </Badge>
                        <Button
                          type='button'
                          variant='tertiary'
                          size='small'
                          onClick={handleManualRefresh}
                          disabled={isRefreshing}
                        >
                          <RotateCcw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      <p className='mt-2 text-sm text-gray-600'>
                        {taskDetail.description}
                      </p>
                    </div>
                    <div className='flex flex-wrap justify-end gap-2'>
                      <TaskActionControls
                        task={taskDetail as unknown as BrokersTask}
                        onTaskAction={handleTaskAction}
                        isLoading={taskActionLoading}
                        workflowId={workflowId}
                        enrollmentId={enrollmentId}
                        withPortal={false}
                      />
                    </div>
                  </div>

                  <div className='mt-4 grid gap-4 md:grid-cols-2'>
                    <div className='text-sm text-gray-600'>
                      <span className='font-medium text-gray-800'>Created</span>{' '}
                      {getFormattedDate(taskDetail.created_at, { hour: 'numeric', minute: '2-digit' })}
                    </div>
                    <div className='text-sm text-gray-600'>
                      <span className='font-medium text-gray-800'>Updated</span>{' '}
                      {getFormattedDate(taskDetail.updated_at, { hour: 'numeric', minute: '2-digit' })}
                    </div>
                    {taskDetail.message && (
                      <div className='md:col-span-2 rounded border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700'>
                        {taskDetail.message}
                      </div>
                    )}
                  </div>

                  <div className='mt-4 flex flex-wrap items-center gap-3 text-sm text-gray-600'>
                    <span>Failures: {taskDetail.fail_count}</span>
                    <span>Sequence: {taskDetail.sequence_order ?? '—'}</span>
                  </div>
                </section>

                {hasPlaywrightExecution && (
                  <section className='rounded-lg border border-gray-200 bg-white p-5 shadow-sm'>
                    <div className='flex flex-wrap items-center justify-between gap-4'>
                      <div>
                        <h3 className='text-sm font-semibold text-gray-900'>Playwright execution</h3>
                        <p className='mt-1 text-xs text-gray-500'>
                          Generate a local command to replay this task via automation.
                        </p>
                      </div>
                      <Badge type='neutral'>
                        {taskDetail.playwright_execution_key}
                      </Badge>
                    </div>

                    <div className='mt-4 grid gap-3 md:grid-cols-3 md:items-end'>
                      <div className='flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-3'>
                        <Label htmlFor='playwright-safety' className='text-sm font-medium text-gray-800'>
                          Safety checks
                        </Label>
                        <Switch
                          id='playwright-safety'
                          checked={includeSafety}
                          onCheckedChange={setIncludeSafety}
                        />
                      </div>
                      <div className='flex items-center justify-between rounded border border-gray-200 bg-gray-50 p-3'>
                        <Label htmlFor='playwright-ignore-hold' className='text-sm font-medium text-gray-800'>
                          Ignore hold
                        </Label>
                        <Switch
                          id='playwright-ignore-hold'
                          checked={ignoreHold}
                          onCheckedChange={setIgnoreHold}
                        />
                      </div>
                    </div>

                    <div className='mt-4'>
                      <Button
                        type='button'
                        onClick={handleCopyPlaywrightCommand}
                        disabled={buildPlaywrightCommandMutation.isPending}
                      >
                        {buildPlaywrightCommandMutation.isPending
                          ? <Loader2 className='h-4 w-4 animate-spin' />
                          : 'Copy Playwright command'}
                      </Button>
                    </div>
                  </section>
                )}

                <CollapsibleContainer
                  heading='Task actions'
                  headingLevel={4}
                  defaultOpen={false}
                >
                  <div className='space-y-6 p-4'>
                    <div className='space-y-2'>
                      <div className='flex flex-wrap items-center justify-between gap-2'>
                        <p className='text-sm text-gray-600'>
                          Review and clear the current lease when automation is stuck.
                        </p>
                        <Badge type={taskDetail.lease_active ? 'warning' : 'neutral'}>
                          {taskDetail.lease_active ? 'Lease active' : 'No active lease'}
                        </Badge>
                      </div>
                      <div className='rounded border border-gray-100 bg-gray-50 p-3 text-xs text-gray-600'>
                        <div className='flex items-center justify-between gap-2'>
                          <span className='font-medium text-gray-800'>Lease holder</span>
                          <span className='truncate'>
                            {taskDetail.lease_holder_name ??
                              (taskDetail.lease_holder ? 'Unknown executor' : 'None')}
                          </span>
                        </div>
                        {taskDetail.lease_holder && (
                          <div className='mt-1 truncate font-mono text-[11px] text-gray-500'>
                            {taskDetail.lease_holder}
                          </div>
                        )}
                        <div className='mt-2 flex items-center justify-between gap-2'>
                          <span className='font-medium text-gray-800'>Expires</span>
                          <span>
                            {taskDetail.lease_expires_at
                              ? getFormattedDate(taskDetail.lease_expires_at, {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })
                              : 'Not set'}
                          </span>
                        </div>
                      </div>
                      <div className='flex flex-wrap items-center gap-2'>
                        <Button
                          type='button'
                          variant='secondary'
                          onClick={() => handleClearLease()}
                          disabled={!hasLease || clearLeaseMutation.isPending}
                        >
                          <span className='flex items-center gap-1'>
                            {clearLeaseMutation.isPending
                              ? (<Loader2 className='mr-2 h-4 w-4 animate-spin' />)
                              : (<RotateCcw className='mr-2 h-4 w-4' />)}
                            Clear lease
                          </span>
                        </Button>
                        {taskDetail.lease_holder_url && (
                          <Button
                            type='button'
                            variant='tertiary'
                            size='small'
                            onClick={() => window.open(taskDetail.lease_holder_url!, '_blank', 'noopener,noreferrer')}
                          >
                            Open GoodJob details
                          </Button>
                        )}
                      </div>
                      {!hasLease && (
                        <p className='text-xs text-gray-500'>
                          No lease is currently held on this task.
                        </p>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <p className='text-sm text-gray-600'>
                        Reset the task back to a pending state so it can be retried.
                      </p>
                      {taskDetail.status === 'processing' && (
                        <Button
                          type='button'
                          variant='primary-destructive'
                          onClick={handleFailTask}
                          disabled={bulkUpdateMutation.isPending}
                        >
                          <span className='flex items-center gap-1'>
                            {bulkUpdateMutation.isPending
                              ? (<Loader2 className='mr-2 h-4 w-4 animate-spin' />)
                              : (<RotateCcw className='mr-2 h-4 w-4' />)}
                            Move task to failed state
                          </span>
                        </Button>
                      )}
                      {taskDetail.status !== 'processing' && (
                        <Button
                          type='button'
                          variant='secondary'
                          onClick={handleResetToPending}
                          disabled={resetTaskMutation.isPending}
                        >
                          <span className='flex items-center gap-1'>
                            {resetTaskMutation.isPending
                              ? (<Loader2 className='mr-2 h-4 w-4 animate-spin' />)
                              : (<RotateCcw className='mr-2 h-4 w-4' />)}
                            Reset to pending
                          </span>
                        </Button>
                      )}
                    </div>

                    <div className='space-y-2'>
                      <p className='text-sm text-gray-600'>
                        Queue this task for automated execution. The task must be pending, automated, and ready to run without blockers.
                      </p>
                      <Button
                        type='button'
                        onClick={handleExecuteTask}
                        disabled={
                          !taskDetail.can_execute ||
                          taskDetail.execution_method !== 'automated' ||
                          executeTaskMutation.isPending
                        }
                      >
                        <span className='flex items-center gap-1'>
                          {executeTaskMutation.isPending
                            ? (<Loader2 className='mr-2 h-4 w-4 animate-spin' />)
                            : (<Play className='mr-2 h-4 w-4' />)}
                          Run automated task
                        </span>
                      </Button>
                      {(!taskDetail.can_execute || taskDetail.execution_method !== 'automated') && (
                        <p className='text-xs text-gray-500'>
                          Task must be pending, automated, and free of blocking dependencies before it can run.
                        </p>
                      )}
                    </div>
                  </div>
                </CollapsibleContainer>

                {renderInstructionsSection()}

                {renderMetadataSection(taskDetail)}

                <CollapsibleContainer
                  heading='Execution history'
                  headingLevel={4}
                  defaultOpen={false}
                  onOpenChange={(open) => {
                    if (open) {
                      setExecutionHistoryVersion((previous) => previous + 1)
                    }
                  }}
                >
                  <div className='p-4'>
                    <TaskResultExplorer
                      taskId={taskId!}
                      collapseVersion={executionHistoryVersion}
                      results={taskResults}
                      isLoading={taskLoading && enabled}
                    />
                  </div>
                </CollapsibleContainer>

                <CollapsibleContainer
                  heading='Task notes'
                  headingLevel={4}
                  defaultOpen={false}
                >
                  <div className='p-4'>
                    <TaskNotes task={taskDetail} displayExistingNotes />
                  </div>
                </CollapsibleContainer>

                <CollapsibleContainer
                  heading='Task audit history'
                  headingLevel={4}
                  defaultOpen={false}
                  onOpenChange={setAuditSectionOpen}
                >
                  <div className='p-4'>
                    <AuditHistoryDrawer
                      params={{
                        auditable_type: 'Tasks::Task',
                        auditable_id: taskDetail.id,
                      }}
                      isOpen={auditSectionOpen}
                      title={`Task Audit History - ${taskDetail.display_name}`}
                      containerClasses='px-0'
                    />
                  </div>
                </CollapsibleContainer>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default TaskDetailsDialog
